import React from 'react';
import { Box, CircularProgress, Stack, Typography, styled } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ReadingRow } from 'components/ProgressTab/WidgetDetail/WidgetReadingList';
import { MealSwatch } from 'components/ProgressTab/BloodGlucoseLegend';
import { WidgetDetailLayout } from 'components/ProgressTab/WidgetDetail/WidgetDetailLayout';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { BiometricDatapointRecord } from 'services/biometricsService';
import { longFormatDate } from 'utils/dateUtils';
import {
  MEAL_LABEL_KEYS,
  MealReading,
  toBloodGlucoseWeekSeries,
} from 'features/progress/utils/bloodGlucoseMeals';
import { getYAxisRange } from 'features/progress/utils/bloodGlucoseChartUtils';
import {
  formatDateRangeLabel,
  toPastReadings,
} from 'features/progress/utils/bloodSugarDetailSeries';
import { useChartReadingWindows } from 'features/progress/hooks/useChartReadingWindows';
import { BloodSugarDetailChart, DetailChartFilter } from './BloodSugarDetailChart';

// A reading with no meal relationship shows a "?" placeholder, as on mobile.
const UnknownMealIcon = styled(HelpOutlineIcon)({ fontSize: 14, marginInline: '-2px' });

export interface BloodSugarDetailViewProps {
  datapoints?: BiometricDatapointRecord[];
  isLoading?: boolean;
  isError?: boolean;
  onBack: () => void;
  onDownload?: () => void;
  isDownloading?: boolean;
}

export const BloodSugarDetailView = ({
  datapoints = [],
  isLoading = false,
  isError = false,
  onBack,
  onDownload,
  isDownloading = false,
}: BloodSugarDetailViewProps) => {
  const { translation } = useTranslationContext();
  const [filter, setFilter] = React.useState<DetailChartFilter>('all');
  const { days, canGoBack, canGoForward, goBack, goForward } = useChartReadingWindows(datapoints);

  const series = React.useMemo(
    () => toBloodGlucoseWeekSeries(datapoints, days),
    [datapoints, days],
  );
  const yRange = React.useMemo(() => getYAxisRange(datapoints), [datapoints]);
  // The legend filter narrows the readings table as well as the chart.
  const pastReadings = React.useMemo(
    () =>
      toPastReadings(datapoints, days).filter(
        (reading) => filter === 'all' || reading.meal === filter,
      ),
    [datapoints, days, filter],
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <Stack alignItems="center" p={6}>
          <CircularProgress aria-label={translation('blood_sugar')} />
        </Stack>
      );
    }

    if (isError) {
      return (
        <Stack alignItems="center" p={6}>
          <Typography variant="footnote">{translation('data_unable_to_load')}</Typography>
        </Stack>
      );
    }

    return (
      <BloodSugarDetailChart
        series={series}
        filter={filter}
        onFilterChange={setFilter}
        yRange={yRange}
      />
    );
  };

  const readings: ReadingRow[] = pastReadings.map((reading) => ({
    id: reading.id,
    value: reading.value,
    unit: 'mg/dL',
    dateLabel: longFormatDate(reading.date),
    label: reading.meal ? translation(MEAL_LABEL_KEYS[reading.meal]) : undefined,
    // A reading with no meal relationship shows a "?" placeholder, as on mobile.
    marker: reading.meal ? (
      <MealSwatch meal={reading.meal} size={12} aria-hidden />
    ) : (
      <UnknownMealIcon aria-hidden />
    ),
  }));

  return (
    <WidgetDetailLayout
      title={translation('blood_sugar')}
      onBack={onBack}
      dateRange={{
        label: formatDateRangeLabel(days),
        onPrevious: goBack,
        onNext: goForward,
        disablePrevious: !canGoBack,
        disableNext: !canGoForward,
        previousAriaLabel: translation('back_1_week_accessibility'),
        nextAriaLabel: translation('forward_1_week_accessibility'),
      }}
      readings={readings}
      onDownload={onDownload}
      isDownloading={isDownloading}
    >
      {renderBody()}
    </WidgetDetailLayout>
  );
};
